// src/components/resources/guides/GuideCard.tsx

'use client'

import { motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import RiTimeLine from '~/icons/remix/generated/RiTimeLine'
import RiArrowRightLine from '~/icons/remix/generated/RiArrowRightLine'
import { type BaseResource } from '~/lib/resources/types/shared/base'
import { formatDate } from '~/lib/utils/date'

interface GuideCardProps {
  guide: BaseResource
  featured?: boolean
  /**
   * "Updated on" date floored by the sitemap/JSON-LD resolver
   * (`resolvedModifiedDate`), computed server-side in the route. Supersedes the
   * raw authored `guide.lastUpdated` so the visible date stays consistent with
   * (never older than) the structured-data date.
   */
  displayLastUpdated?: string
}

export function GuideCard({
  guide,
  featured = false,
  displayLastUpdated,
}: GuideCardProps): JSX.Element {
  const lastUpdated = displayLastUpdated ?? guide.lastUpdated
  return (
    <Link href={`/resources/guides/${guide.slug}`}>
      <motion.article
        whileHover={{ y: -4 }}
        className={`group relative flex flex-col overflow-hidden rounded-2xl bg-white shadow-md ring-1 ring-gray-200 transition-all hover:shadow-xl dark:bg-gray-800/50 dark:ring-gray-800 dark:hover:bg-gray-800 ${
          featured ? 'lg:col-span-2' : ''
        }`}
      >
        {guide.images && (
          <div className="relative aspect-[16/9] overflow-hidden">
            <Image
              src={
                guide.images.thumbnail?.url ??
                guide.images.hero?.url ??
                guide.images.og?.url ??
                '/images/resources/guides/adsights-digital-marketing-resource-center-guides-tutorials-hero-image.webp'
              }
              alt={guide.images.hero?.alt ?? guide.title}
              fill
              className="object-cover transition-transform duration-300 group-hover:scale-105"
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-transparent" />
            
            {/* Category Badge */}
            {guide.category && (
              <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-sm font-medium text-gray-900 backdrop-blur dark:bg-gray-900/90 dark:text-white">
                {guide.category}
              </div>
            )}

            {featured && (
              <div className="absolute right-4 top-4 rounded-full bg-indigo-600/90 px-3 py-1 text-sm font-medium text-white backdrop-blur">
                Featured
              </div>
            )}
          </div>
        )}

        <div className="flex flex-1 flex-col p-6">
          <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
            {lastUpdated && (
              <time dateTime={lastUpdated} className="flex items-center gap-1.5">
                <RiTimeLine className="h-4 w-4" />
                {formatDate(lastUpdated)}
              </time>
            )}
            {guide.estimatedTime && (
              <>
                <span className="text-gray-300 dark:text-gray-600">•</span>
                <span>{guide.estimatedTime} read</span>
              </>
            )}
          </div>

          <h3 className="mt-4 text-xl font-semibold text-gray-900 group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
            {guide.canonicalTitle ?? guide.title}
          </h3>

          <p className="mt-3 line-clamp-2 flex-1 text-base text-gray-600 dark:text-gray-400">
            {guide.description}
          </p>

          <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300">
            Read more
            <RiArrowRightLine className="h-4 w-4" />
          </div>
        </div>
      </motion.article>
    </Link>
  )
} 